import React from 'react';

import {withCssModule} from '@pb/kit-util-with-css-module';

import styles from './menu.module.styl';

import type {ComponentPropsWithRef} from 'react';


const classNames = withCssModule(styles);

export interface MenuItemBaseProps extends ComponentPropsWithRef<'div'> {
    /**
     * When `true`, applies the `item__highlighted` CSS class (keyboard focus
     * or mouse hover equivalent in the virtual-focus model). Managed by
     * `FloatingMenuItem` — do not set manually when using that component.
     */
    highlighted?: boolean,

    /**
     * When `true`, applies `item_disabled`, sets `aria-disabled="true"`, and
     * suppresses click/Enter handlers so the item cannot be activated.
     */
    disabled?: boolean,

    /**
     * When defined, switches the role to `menuitemcheckbox` and reflects the
     * value via `aria-checked` to mark the item as the current selection.
     * Leave `undefined` for a plain `menuitem`.
     */
    selected?: boolean,
}

/**
 * Primitive `<div role="menuitem">` that applies hover, focus, disabled, and
 * selected CSS states. Use this as the lowest-level building block when
 * composing custom menu items; prefer `<MenuItem>` for standard caption/icon
 * layouts.
 */
export function MenuItemBase(props: MenuItemBaseProps) {
    const {
        ref,
        disabled,
        selected,
        children,
        highlighted,
        className,
        ...rootProps
    } = props;
    const itemClasses = classNames('item', className, {
        item_disabled: disabled,
        selected,
        item__highlighted: highlighted,
    });

    return (
        <div
            role={selected !== undefined ? 'menuitemcheckbox' : 'menuitem'}
            aria-checked={selected}
            aria-disabled={disabled}
            {...rootProps}
            className={itemClasses}
            ref={ref}>
            {children}
        </div>
    );
}
